import { Injectable } from '@angular/core';
import { MegaMenuColumn, MenuItem } from '../../models';
import {
    Project,
    ProjectStatus,
    ProjectType,
    Release,
    System,
    ApplicationConfig,
    SystemTypes,
    SystemDetails
} from '@frontend-site/shared/data';
import { RECENT_ITEMS_COUNT } from '../menu.service';
import { PermissionsService, ProjectStore, ReleaseStore, SessionRepository, SystemStore } from '@frontend-site/shared/core';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class MegaMenuBuilderService {
    constructor(
        private readonly systemStore: SystemStore,
        private readonly releaseStore: ReleaseStore,
        private readonly sessionStore: SessionRepository,
        private readonly projectStore: ProjectStore,
        private readonly permissionsService: PermissionsService,
        private readonly translate: TranslateService
    ) {}

    build$(): Observable<MegaMenuColumn[]> {
        return this.sessionStore.applicationConfig$.pipe(
            switchMap((appConfig) =>
                combineLatest([
                    this.projectStore.projects$,
                    this.systemStore.systems$,
                    this.releaseStore.releases$,
                    this.systemStore.systemsDetails$,
                    this.projectStore.projectDetails$
                ]).pipe(
                    switchMap(([projects, systems, releases, systemsDetails, projectInfo]) =>
                        of({ projects, systems, releases, appConfig, systemsDetails, projectInfo })
                    )
                )
            ),
            map(({ projects, systems, releases, systemsDetails, appConfig, projectInfo }) => {
                const megaMenuColumns: MegaMenuColumn[] = [];
                megaMenuColumns.push(this.buildProjectsColumn(projects, appConfig));
                if (appConfig?.rdxEnabled && releases) {
                    megaMenuColumns.push(this.buildReleasesColumn(releases, appConfig));
                }
                const systemsColumn = this.buildSystemsColumn(systems, appConfig, systemsDetails);
                if (systemsColumn.items.length > 0) {
                    megaMenuColumns.push(systemsColumn);
                }
                megaMenuColumns.push(this.buildAccountColumn(appConfig));

                return megaMenuColumns;
            })
        );
    }

    private buildProjectsColumn(projects: Project[], appConfig: ApplicationConfig | undefined): MegaMenuColumn {
        const projectColumn: MegaMenuColumn = {
            header: 'Layouts.Masthead.COL_MENU.COLUMN_HEADER.PROJECTS',
            icon: 'home',
            items: projects && appConfig?.companyId ? this.buildProjectMenuItems(projects, appConfig) : [],
            footer: 'Layouts.Masthead.COL_MENU.COLUMN_SUMMARY.ALL_PROJECTS',
            footerRoutePath: `/project`
        };
        return projectColumn;
    }

    private buildReleasesColumn(releases: Release[], appConfig: ApplicationConfig): MegaMenuColumn {
        const filteredReleases = this.getMostRecentReleases(releases);
        const releasesColumn: MegaMenuColumn = {
            header: 'Layouts.Masthead.COL_MENU.COLUMN_HEADER.RELEASES',
            icon: 'rocket',
            items: releases ? this.buildReleasesMenuItems(filteredReleases, appConfig?.companyId) : [],
            footer: 'Layouts.Masthead.COL_MENU.COLUMN_SUMMARY.ALL_RELEASES',
            footerRoutePath: `/releaseDynamix/${appConfig.companyId}/contextSelector/chart`,
            reload: true
        };
        return releasesColumn;
    }

    private buildSystemsColumn(
        systems: System[],
        appConfig: ApplicationConfig | undefined,
        systemsDetails: SystemDetails[]
    ): MegaMenuColumn {
        const filteredSystems = systems.slice(0, RECENT_ITEMS_COUNT).filter((system) => {
            const systemDetails = systemsDetails.find((s) => s.systemId === system.id);
            if (system?.accountType === SystemTypes.SALESFORCE) return true;
            if (appConfig?.uatOnly) return false;
            return (
                appConfig?.iaEnabled ||
                appConfig?.rdxEnabled ||
                (systemDetails && systemDetails.systemEtlExist && !systemDetails.systemDummyEtl && !appConfig?.rdxEnabled)
            );
        });
        const systemsColumn: MegaMenuColumn = {
            header: 'Layouts.Masthead.COL_MENU.COLUMN_HEADER.SYSTEMS',
            icon: 'system',
            items: this.buildSystemsMenuItems(filteredSystems, appConfig),
            footer: 'Layouts.Masthead.COL_MENU.COLUMN_SUMMARY.ALL_SYSTEMS',
            footerRoutePath: '/systems'
        };
        return systemsColumn;
    }

    private buildAccountColumn(appConfig?: ApplicationConfig): MegaMenuColumn {
        const portfolioColumn: MegaMenuColumn = {
            header: 'Layouts.Masthead.COL_MENU.COLUMN_HEADER.ACCOUNT',
            icon: 'account',
            items: [
                {
                    label: appConfig?.companyName ?? '',
                    routePath: `/project`
                }
            ],
            footer: ''
        };
        return portfolioColumn;
    }

    private buildProjectMenuItems(projects: Project[], appConfig: ApplicationConfig): MenuItem[] {
        return this.getAllowedRecentProjects(projects, appConfig).map((project) => ({
            label: project.projectName,
            routePath: `/enterProject/${project.projectId}/${project.projectType}/${project.systemId}`
        }));
    }

    private buildReleasesMenuItems(releases: Release[], companyId: number): MenuItem[] {
        return releases.map((release) => ({
            label: release.name,
            routePath: `/enterRelease/${companyId}/${release.id}`
        }));
    }

    private buildSystemsMenuItems(systems: System[], appConfig: ApplicationConfig | undefined): MenuItem[] {
        return systems?.map((system) => ({
            label: system.name,
            routePath: `/enterSystem/${system.id}`
        }));
    }

    private getAllowedRecentProjects(allProjects: Project[], appConfig: ApplicationConfig): Project[] {
        return allProjects
            .filter((project) => appConfig.companyId === project.companyId && this.shouldAllowEnterProject(project, appConfig))
            .sort(function (a, b) {
                return b.lastLoginDate - a.lastLoginDate;
            })
            .slice(0, RECENT_ITEMS_COUNT);
    }

    private shouldAllowEnterProject(project: Project, appConfig: ApplicationConfig): boolean {
        return (
            !(!project.ntm && !appConfig.oldUIAvailable) &&
            project.projectAvailabilityStatus != ProjectStatus.PROCESSING &&
            (project.projectAvailabilityStatus != ProjectStatus.UNAVAILABLE ||
                (project.projectAvailabilityStatus === ProjectStatus.UNAVAILABLE && appConfig.isExecuteUser)) &&
            project.projectType != ProjectType.STANDALONE_PROJECT
        );
    }

    private getMostRecentReleases(allReleases: Release[]): Release[] {
        const currentRelease = this.getCurrentRelease(allReleases);
        const halfwayMostRecentFutureReleases = Math.floor(RECENT_ITEMS_COUNT / 2);
        const halfwayMostRecentPastReleases =
            RECENT_ITEMS_COUNT % 2 ? halfwayMostRecentFutureReleases : halfwayMostRecentFutureReleases - 1;
        const currentReleaseDate: Date = new Date(currentRelease ? currentRelease.releaseDate : Date());
        let futureReleases = allReleases.filter((re) => re.id != -1 && new Date(re.releaseDate) > currentReleaseDate);
        let pastReleases = allReleases.filter((re) => re.id != -1 && new Date(re.releaseDate) < currentReleaseDate);

        // In case we have enough future releases and enough past releases
        // in order to place current releases in the middle
        if (futureReleases.length >= halfwayMostRecentFutureReleases && pastReleases.length >= halfwayMostRecentPastReleases) {
            futureReleases = futureReleases.slice(futureReleases.length - halfwayMostRecentFutureReleases, futureReleases.length);
            pastReleases = pastReleases.slice(0, halfwayMostRecentPastReleases);
        } else if (futureReleases.length < halfwayMostRecentFutureReleases) {
            pastReleases = pastReleases.slice(0, RECENT_ITEMS_COUNT - 1 - futureReleases.length);
        } else if (pastReleases.length < halfwayMostRecentFutureReleases) {
            futureReleases = futureReleases.slice(0, RECENT_ITEMS_COUNT - 1 - pastReleases.length);
        }
        const closeToCurrentRelease: Release[] = [];
        closeToCurrentRelease.push(...futureReleases);
        currentRelease && closeToCurrentRelease.push({ ...currentRelease });
        closeToCurrentRelease.push(...pastReleases);
        return closeToCurrentRelease;
    }

    private getCurrentRelease(allReleases: Release[]): Release | undefined {
        return allReleases.find((release) => release.current === true);
    }

    private hasPortfolioPermission(): boolean {
        return this.permissionsService.hasPermissions('Portfolio : view');
    }
}
